import type { NewspaperResult } from '@creanizer/types'
import { getStylesForType } from '../utils/visualStyles'
import '../styles/documentStyles.css'

interface NewspaperViewerProps {
  document: NewspaperResult
  visualStyle?: string
}

export function NewspaperViewer({ document, visualStyle = 'modern' }: NewspaperViewerProps) {
  // データ検証
  if (!document || !document.headline || !document.body) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">新聞記事</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  // 日付フォーマット
  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      })
    } catch {
      return dateStr
    }
  }
  
  // 本文の段落分割
  const formatBody = (body: string): string[] => {
    return body.split('\n\n').filter(p => p.trim())
  }
  
  // ビジュアルスタイルの取得
  const styles = getStylesForType('newspaper')
  const selectedStyle = styles.find(s => s.id === visualStyle) || styles[0]
  const styleClass = selectedStyle.className
  
  // スタイル別のレンダリング
  if (visualStyle === 'classic') {
    // クラシック新聞（20世紀中期）
    return (
      <div className={`max-w-5xl mx-auto p-8 shadow-lg ${styleClass}`}>
        <header className="border-b-4 border-double border-gray-800 pb-4 mb-6">
          <h1 className="text-6xl font-serif text-center font-bold text-gray-900">
            {document.publication || '極東日報'}
          </h1>
          <div className="flex justify-between items-center mt-3 text-sm">
            <span>{formatDate(document.date)}</span>
            <span className="font-bold">{document.section || '特報'}</span>
          </div>
        </header>
        <article>
          <h2 className="text-4xl font-bold font-serif mb-3 text-center">
            {document.headline}
          </h2>
          {document.subheadline && (
            <h3 className="text-xl mb-4 text-center font-serif">
              {document.subheadline}
            </h3>
          )}
          <div className="text-base leading-relaxed font-serif">
            {document.lead && (
              <p className="font-bold mb-4 text-lg">{document.lead}</p>
            )}
            {formatBody(document.body).map((paragraph, index) => (
              <p key={index} className="mb-3 text-justify">
                {paragraph}
              </p>
            ))}
          </div>
        </article>
      </div>
    )
  }
  
  if (visualStyle === 'vintage') {
    // ビンテージ新聞（戦前）
    return (
      <div className={`max-w-5xl mx-auto p-8 shadow-lg relative ${styleClass}`}>
        <header className="border-b-8 border-double border-yellow-900/50 pb-4 mb-6">
          <h1 className="text-5xl font-bold text-center" style={{ fontFamily: 'serif' }}>
            {document.publication || '帝國新聞'}
          </h1>
          <div className="text-center mt-2 text-sm">
            <span>{formatDate(document.date)}</span>
          </div>
        </header>
        <article>
          <h2 className="text-3xl font-bold mb-3 text-center" style={{ fontFamily: 'serif' }}>
            {document.headline}
          </h2>
          {document.subheadline && (
            <h3 className="text-lg mb-4 text-center">{document.subheadline}</h3>
          )}
          <div className="text-sm leading-relaxed columns-2 gap-8">
            {document.lead && (
              <p className="font-bold mb-3">{document.lead}</p>
            )}
            {formatBody(document.body).map((paragraph, index) => (
              <p key={index} className="mb-3 text-justify">
                {paragraph}
              </p>
            ))}
          </div>
        </article>
      </div>
    )
  }
  
  if (visualStyle === 'war') {
    // 戦時新聞
    return (
      <div className={`max-w-5xl mx-auto p-8 shadow-lg relative ${styleClass}`}>
        <header className="border-b-4 border-gray-600 pb-4 mb-6">
          <h1 className="text-5xl font-bold text-center text-gray-900">
            {document.publication || '大本営発表'}
          </h1>
          <div className="text-center mt-2 text-sm">
            <span>{formatDate(document.date)}</span>
          </div>
        </header>
        <article>
          <h2 className="text-3xl font-bold mb-3 text-center">
            {document.headline}
          </h2>
          <div className="text-base leading-relaxed">
            {document.lead && (
              <p className="font-bold mb-4">{document.lead}</p>
            )}
            {formatBody(document.body).map((paragraph, index) => (
              <p key={index} className="mb-3">
                {paragraph}
              </p>
            ))}
          </div>
        </article>
      </div>
    )
  }
  
  if (visualStyle === 'tabloid') {
    // タブロイド紙
    return (
      <div className={`max-w-4xl mx-auto p-6 shadow-lg ${styleClass}`}>
        <header className="mb-6">
          <h1 className="text-6xl font-black text-center text-red-600" style={{ fontFamily: 'Impact, sans-serif' }}>
            {document.publication || 'SHOCKING NEWS'}
          </h1>
          <div className="text-center mt-2 text-sm text-gray-700">
            <span>{formatDate(document.date)}</span>
          </div>
        </header>
        <article>
          <h2 className="text-5xl font-black mb-4 text-center text-red-700 leading-tight">
            {document.headline}
          </h2>
          {document.subheadline && (
            <h3 className="text-2xl font-bold mb-4 text-center text-gray-800">
              {document.subheadline}
            </h3>
          )}
          <div className="text-lg leading-relaxed">
            {document.lead && (
              <p className="font-bold text-xl mb-4 text-red-600">{document.lead}</p>
            )}
            {formatBody(document.body).map((paragraph, index) => (
              <p key={index} className="mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        </article>
      </div>
    )
  }
  
  // デフォルト（現代新聞）
  return (
    <div className="newspaper-container max-w-5xl mx-auto bg-[#faf8f3] p-8 shadow-lg">
      {/* 新聞ヘッダー */}
      <header className="border-b-4 border-double border-gray-800 pb-4 mb-6">
        <h1 className="text-5xl md:text-6xl font-serif text-center font-bold text-gray-900 tracking-wide">
          {document.publication || '架空新聞'}
        </h1>
        <div className="flex justify-between items-center mt-3 text-sm text-gray-700">
          <span className="font-serif">{document.section || '総合'}</span>
          <span className="font-serif font-medium">{formatDate(document.date)}</span>
          <span className="font-serif">第1面</span>
        </div>
      </header>
      
      {/* メイン記事 */}
      <article className="newspaper-article">
        {/* 見出し */}
        <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3 leading-tight text-gray-900">
          {document.headline}
        </h2>
        
        {/* サブ見出し */}
        {document.subheadline && (
          <h3 className="text-xl md:text-2xl font-serif mb-4 text-gray-800 border-l-4 border-gray-400 pl-3">
            {document.subheadline}
          </h3>
        )}
        
        {/* リード文 */}
        {document.lead && (
          <div className="bg-gray-100 border-l-4 border-gray-600 p-4 mb-6">
            <p className="text-lg font-serif font-medium leading-relaxed text-gray-800">
              {document.lead}
            </p>
          </div>
        )}
        
        {/* 本文（マルチカラム） */}
        <div className="newspaper-columns">
          {formatBody(document.body).map((paragraph, index) => (
            <p
              key={index}
              className="font-serif text-justify mb-4 text-gray-900 leading-relaxed first-letter:text-5xl first-letter:font-bold first-letter:float-left first-letter:mr-2 first-letter:mt-1"
            >
              {paragraph}
            </p>
          ))}
        </div>
        
        {/* 引用 */}
        {document.quotes && document.quotes.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 border-l-4 border-blue-600">
            <h4 className="font-serif font-bold text-gray-800 mb-3">関係者の声</h4>
            {document.quotes.map((quote, index) => (
              <div key={index} className="mb-3">
                <p className="font-serif italic text-gray-700">
                  「{quote.text}」
                </p>
                <p className="text-sm font-serif text-gray-600 mt-1 text-right">
                  ― {quote.source}
                </p>
              </div>
            ))}
          </div>
        )}
        
        {/* 関連記事 */}
        {document.relatedArticles && document.relatedArticles.length > 0 && (
          <div className="mt-6 p-3 bg-gray-100 border border-gray-300">
            <h4 className="font-serif font-bold text-sm text-gray-700 mb-2">関連記事</h4>
            <ul className="space-y-1">
              {document.relatedArticles.map((article, index) => (
                <li key={index} className="text-sm font-serif text-blue-700 hover:underline cursor-pointer">
                  ▶ {article}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* 記者署名 */}
        {document.author && (
          <div className="mt-6 text-sm text-right font-serif text-gray-600 border-t border-gray-300 pt-3">
            {document.author.title && (
              <span className="mr-2">{document.author.title}</span>
            )}
            <span className="font-medium">{document.author.name || '記者'}</span>
          </div>
        )}
      </article>
      
      <style>{`
        .newspaper-columns {
          column-count: 3;
          column-gap: 2rem;
          column-rule: 1px solid #d1d5db;
        }
        
        @media (max-width: 1024px) {
          .newspaper-columns {
            column-count: 2;
          }
        }
        
        @media (max-width: 640px) {
          .newspaper-columns {
            column-count: 1;
          }
        }
        
        .newspaper-columns p {
          break-inside: avoid;
        }
      `}</style>
    </div>
  )
}